import { useEffect, useRef, useState } from 'react'
import { Box, CssBaseline, ThemeProvider, createTheme } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import Beacon4FilterBar              from './components/filters/Beacon4FilterBar'
import Beacon4LegendPanel            from './components/filters/Beacon4LegendPanel'
import Beacon4PerformanceLegendPanel from './components/filters/Beacon4PerformanceLegendPanel'
import Beacon4BlendedView            from './components/Beacon4BlendedView'
import Beacon4PerformanceView        from './components/Beacon4PerformanceView'
import Beacon4ExportButton           from './components/Beacon4ExportButton'
import { selectBeacon4Module }                  from './reducers/Beacon4FiltersReducer'
import { loadBeacon4PerformanceAvailableKpis } from './reducers/Beacon4PerformanceKpiDataReducer'

const theme = createTheme({
  typography: { fontFamily: "'IBM Plex Sans', sans-serif" },
  palette:    { primary: { main: '#1565c0' }, background: { default: '#f0f2f5' } },
})

// Default/bounds for the user-resizable legend panel (in pixels). Each
// module keeps its own previous default (190 for Blended, 220 for
// Performance) so introducing the resize handle doesn't shift either
// panel's initial width.
const LEGEND_MIN_W = 160
const LEGEND_MAX_W = 420
const LEGEND_DEFAULT_W = { Blended: 190, Performance: 220 }

/**
 * Resize handle placed on the boundary between the chart/table column and
 * the legend panel. Dragging it directly controls the LEGEND's width —
 * dragging left (toward the charts) grows the legend, dragging right
 * shrinks it. Mirrors the ChartResizeHandle in Beacon4PerformanceView
 * (same plain, always-visible grey bar; double-click resets to default).
 */
function LegendResizeHandle({ onResize, onReset }) {
  const draggingRef = useRef(false)
  const lastXRef     = useRef(0)
  const [dragging, setDragging] = useState(false)

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!draggingRef.current) return
      const delta = e.clientX - lastXRef.current
      lastXRef.current = e.clientX
      // Dragging LEFT (delta < 0) grows the legend; dragging RIGHT shrinks
      // it — the legend sits on the right edge, so moving the handle left
      // takes width from the chart area and gives it to the legend.
      onResize(w => Math.min(LEGEND_MAX_W, Math.max(LEGEND_MIN_W, w - delta)))
    }
    const onMouseUp = () => {
      if (!draggingRef.current) return
      draggingRef.current = false
      setDragging(false)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [onResize])

  const onMouseDown = (e) => {
    draggingRef.current = true
    setDragging(true)
    lastXRef.current = e.clientX
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }

  return (
    <Box
      onMouseDown={onMouseDown}
      onDoubleClick={onReset}
      title="Drag to resize legend · double-click to reset"
      sx={{
        flexShrink: 0,
        width: 6,
        mx: 0.75,
        borderRadius: 1,
        cursor: 'col-resize',
        bgcolor: dragging ? 'grey.400' : 'grey.200',
        '&:hover': { bgcolor: 'grey.400' },
      }}
    />
  )
}

export default function App({ externalRole }) {
  // Wraps BOTH the chart/table area AND the legend panel — not just the
  // chart area. Beacon4ExportButton captures whatever this ref points to;
  // if it only wrapped the chart column, the legend Drawer (a sibling, not
  // a descendant, of that column) would never be included in the page-level
  // export, leaving the exported charts with colored lines nobody can
  // decode. No changes needed inside Beacon4ExportButton itself — it just
  // needs a container ref that actually contains everything.
  const exportAreaRef = useRef(null)
  const dispatch       = useDispatch()
  const module          = useSelector(selectBeacon4Module)

  // Legend width is kept per-module (see LEGEND_DEFAULT_W) so switching
  // between Blended and Performance doesn't carry one panel's manual resize
  // over to the other.
  const [blendedLegendW, setBlendedLegendW] = useState(LEGEND_DEFAULT_W.Blended)
  const [perfLegendW, setPerfLegendW]       = useState(LEGEND_DEFAULT_W.Performance)
  const setLegendWidth   = module === 'Performance' ? setPerfLegendW : setBlendedLegendW
  const resetLegendWidth = () => setLegendWidth(LEGEND_DEFAULT_W[module])

  // Load the techs + main-KPIs lookup whenever the Performance module is active.
  // The backend caches this for 5 min, so toggling back and forth is cheap.
  useEffect(() => {
    if (module === 'Performance') dispatch(loadBeacon4PerformanceAvailableKpis())
  }, [dispatch, module])

  // Try to fill the height the host shell gives us. If the shell's
  // container doesn't define a height, height: 100% collapses to 0,
  // so we also set a minHeight based on the viewport minus an estimate
  // of the shell's chrome (~120px for topbar + a bit of breathing room).
  // Adjust the constant below if your shell is taller/shorter.
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{
        height: '100%',
        minHeight: 'calc(100vh - 120px)',
        display: 'flex', flexDirection: 'column',
        bgcolor: 'background.default',
      }}>
        <Box sx={{ flexShrink: 0 }}>
          <Beacon4FilterBar
            exportButton={<Beacon4ExportButton containerRef={exportAreaRef} />}
            externalRole={externalRole}
          />
        </Box>

        {/* Body = remaining height. minHeight: 0 lets it shrink properly
            inside the flex column. This whole row (chart/table + legend
            panel) is what gets captured for the page-level export. */}
        <Box ref={exportAreaRef} sx={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
          {/* Chart area. For Performance we hand the scroll over to the
              PerformanceView itself (it sizes the table via autoHeight and
              scrolls internally). For Blended we keep the previous
              outer-scroll behaviour. */}
          <Box
            sx={{
              flex: 1, minWidth: 0, minHeight: 0,
              p: '12px 16px',
              overflowY: module === 'Performance' ? 'hidden' : 'auto',
              display: 'flex', flexDirection: 'column',
            }}
          >
            {module === 'Performance'
              ? <Beacon4PerformanceView externalRole={externalRole} />
              : <Beacon4BlendedView />}
          </Box>
          <LegendResizeHandle onResize={setLegendWidth} onReset={resetLegendWidth} />
          {module === 'Performance'
            ? <Beacon4PerformanceLegendPanel width={perfLegendW} />
            : <Beacon4LegendPanel width={blendedLegendW} />}
        </Box>
      </Box>
    </ThemeProvider>
  )
}